import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import ProductCard from '../components/ProductCard';
import ProductReviews from '../components/ProductReviews';
import { formatINR, finalPrice as calcFinal } from '../utils/priceFormat';

const PH = 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=600&q=80';
const resolveImg = ph => !ph ? PH : ph.startsWith('http') ? ph : `https://shop.venkatagirihandlooms.com/${ph.replace(/^\/+/,'')}`;

export default function ProductPage() {
  const { slug }      = useParams();
  const { user }      = useAuth();
  const { addToCart } = useCart();
  const navigate      = useNavigate();

  // ── ALL hooks declared first — unconditionally ─────────────
  const [product,   setProduct]   = useState(null);
  const [related,   setRelated]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [qty,       setQty]       = useState(1);
  const [selPhoto,  setSelPhoto]  = useState(0);
  const [tab,       setTab]       = useState('description');
  const [pincode,   setPincode]   = useState('');
  const [pinMsg,    setPinMsg]    = useState('');
  const [sticky,    setSticky]    = useState(false);
  const [zoomOpen,  setZoomOpen]  = useState(false);
  const [zoomPhoto, setZoomPhoto] = useState(0);

  // Swipe refs — must be declared here, not after early return
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const zTouchStartX = useRef(null);
  const zTouchStartY = useRef(null);

  // Load product
  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setSelPhoto(0);
    api.get(`/products/${slug}`)
      .then(res => {
        setProduct(res.data);
        if (res.data.cat_id) {
          api.get(`/products?cat_id=${res.data.cat_id}&limit=5`)
            .then(r => setRelated((r.data.products || []).filter(p => p.id !== res.data.id).slice(0, 4)));
        }
      })
      .catch(() => navigate('/collections'))
      .finally(() => setLoading(false));
  }, [slug]);

  // Sticky bar
  useEffect(() => {
    const fn = () => setSticky(window.scrollY > 500);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Escape closes zoom
  useEffect(() => {
    const fn = e => { if (e.key === 'Escape') setZoomOpen(false); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, []);

  // ── Early returns AFTER all hooks ─────────────────────────
  if (loading) return <div style={{ paddingTop: 110 }}><div className="spinner" /></div>;
  if (!product) return null;

  // Derived values
  const photos     = (product.photos?.length ? product.photos : [PH]).map(resolveImg);
  const finalPrice = calcFinal(product.selling_price, product.discount_pct);
  const tags       = Array.isArray(product.tags) ? product.tags : (product.tags || '').split(',');
  const totalPhotos = photos.length;

  // Navigation helpers
  const prevPhoto = () => setSelPhoto(i => (i - 1 + totalPhotos) % totalPhotos);
  const nextPhoto = () => setSelPhoto(i => (i + 1) % totalPhotos);
  const prevZoom  = () => setZoomPhoto(i => (i - 1 + totalPhotos) % totalPhotos);
  const nextZoom  = () => setZoomPhoto(i => (i + 1) % totalPhotos);
  const openZoom  = idx => { setZoomPhoto(idx); setZoomOpen(true); };

  // Swipe handlers — gallery
  const onMainTouchStart = e => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onMainTouchEnd = e => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    touchStartX.current = null;
    if (dy > 60) return; // vertical scroll — ignore
    if (dx < -50) nextPhoto();
    if (dx >  50) prevPhoto();
  };

  // Swipe handlers — zoom
  const onZoomTouchStart = e => {
    zTouchStartX.current = e.touches[0].clientX;
    zTouchStartY.current = e.touches[0].clientY;
  };
  const onZoomTouchEnd = e => {
    if (zTouchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - zTouchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - zTouchStartY.current);
    zTouchStartX.current = null;
    if (dy > 60) return;
    if (dx < -50) nextZoom();
    if (dx >  50) prevZoom();
  };

  // Action handlers
  const handleAddToCart = async () => {
    if (!user) { toast.error('Please login to add to cart'); navigate('/login'); return; }
    try { await addToCart(product.id, qty); toast.success('Added to cart! 🛒'); }
    catch (err) { toast.error(err.message); }
  };
  const handleWishlist = async () => {
    if (!user) { toast.error('Please login'); navigate('/login'); return; }
    try { await api.post('/wishlist', { product_id: product.id }); toast.success('Saved to wishlist ❤️'); }
    catch (err) { toast.error(err.message); }
  };
  const handleBuyNow = async () => {
    try { await handleAddToCart(); navigate('/checkout'); }
    catch (e) { /* handleAddToCart already shows toast */ }
  };

  const checkPin = () => setPinMsg(pincode.length === 6 ? '✅ Delivery available in 4–6 business days.' : 'Enter a valid 6-digit pincode');

  // ── RENDER ────────────────────────────────────────────────
  return (
    <div className="page">
      {/* Breadcrumb */}
      <div style={{ background:'#fff', borderBottom:'1px solid var(--border)', padding:'10px 0' }}>
        <div className="container" style={{ display:'flex', gap:8, fontSize:11, color:'#8a7968', alignItems:'center', flexWrap:'wrap' }}>
          <Link to="/" style={{ color:'#8a7968' }}>Home</Link><span>›</span>
          <Link to="/collections" style={{ color:'#8a7968' }}>Collections</Link>
          {product.category_name && <><span>›</span><Link to={`/collections/${product.category_slug}`} style={{ color:'#8a7968' }}>{product.category_name}</Link></>}
          <span>›</span>
          <span style={{ color:'#7a1f1f', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{product.name}</span>
        </div>
      </div>

      <div className="container" style={{ padding:'28px 16px 72px' }}>
        <div className="pp-grid">

          {/* ═══ IMAGE COLUMN ═══ */}
          <div>
            {/* ─ Desktop: side thumbnails + main ─ */}
            <div className="pp-desk-gallery">
              {/* Thumbnail strip */}
              <div style={{ display:'flex', flexDirection:'column', gap:8, width:74, flexShrink:0 }}>
                {photos.map((ph, i) => (
                  <div key={i} onClick={() => setSelPhoto(i)}
                    style={{ width:74, height:90, cursor:'pointer', overflow:'hidden', border: selPhoto===i ? '2px solid #7a1f1f' : '2px solid #e8ddd0', flexShrink:0 }}>
                    <img src={ph} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => { e.target.src = PH; }} />
                  </div>
                ))}
              </div>
              {/* Main image */}
              <div style={{ flex:1, position:'relative', background:'#f7f0e6', overflow:'hidden' }}>
                <img src={photos[selPhoto]} alt={product.name}
                  style={{ width:'100%', aspectRatio:'3/4', objectFit:'cover', cursor:'zoom-in', display:'block' }}
                  onError={e => { e.target.src = PH; }} onClick={() => openZoom(selPhoto)} />
                {product.discount_pct > 0 && <div style={{ position:'absolute', top:12, left:12, background:'#7a1f1f', color:'#fff', padding:'4px 10px', fontSize:10, fontWeight:700 }}>-{product.discount_pct}%</div>}
                {product.is_new && !product.discount_pct && <div style={{ position:'absolute', top:12, left:12, background:'#c9a84c', color:'#1a0f0f', padding:'4px 10px', fontSize:10, fontWeight:700 }}>NEW</div>}
                <button onClick={() => openZoom(selPhoto)}
                  style={{ position:'absolute', bottom:12, right:12, background:'rgba(255,255,255,0.92)', border:'none', borderRadius:'50%', width:38, height:38, cursor:'pointer', fontSize:16, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 10px rgba(0,0,0,0.15)' }}>🔍</button>
                {totalPhotos > 1 && <>
                  <button onClick={prevPhoto} style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', background:'rgba(255,255,255,0.85)', border:'none', width:32, height:32, borderRadius:'50%', cursor:'pointer', fontSize:18, display:'flex', alignItems:'center', justifyContent:'center' }}>‹</button>
                  <button onClick={nextPhoto} style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'rgba(255,255,255,0.85)', border:'none', width:32, height:32, borderRadius:'50%', cursor:'pointer', fontSize:18, display:'flex', alignItems:'center', justifyContent:'center' }}>›</button>
                </>}
              </div>
            </div>

            {/* ─ Mobile: full-width swipeable ─ */}
            <div className="pp-mob-gallery">
              <div style={{ position:'relative', width:'100%', background:'#f7f0e6', overflow:'hidden', touchAction:'pan-y' }}
                onTouchStart={onMainTouchStart} onTouchEnd={onMainTouchEnd}>
                <img src={photos[selPhoto]} alt={product.name}
                  style={{ width:'100%', aspectRatio:'1/1', objectFit:'cover', display:'block', WebkitUserSelect:'none', userSelect:'none' }}
                  onError={e => { e.target.src = PH; }} onClick={() => openZoom(selPhoto)} />
                {/* Badge */}
                {product.discount_pct > 0 && <div style={{ position:'absolute', top:10, left:10, background:'#7a1f1f', color:'#fff', padding:'3px 9px', fontSize:10, fontWeight:700 }}>-{product.discount_pct}%</div>}
                {product.is_new && !product.discount_pct && <div style={{ position:'absolute', top:10, left:10, background:'#c9a84c', color:'#1a0f0f', padding:'3px 9px', fontSize:10, fontWeight:700 }}>NEW</div>}
                {/* Counter */}
                <div style={{ position:'absolute', bottom:12, right:12, background:'rgba(0,0,0,0.45)', color:'#fff', fontSize:11, padding:'3px 10px', borderRadius:12, fontFamily:"'Jost',sans-serif" }}>
                  {selPhoto+1}/{totalPhotos} · Tap to zoom
                </div>
                {/* Dot indicators */}
                {totalPhotos > 1 && (
                  <div style={{ position:'absolute', bottom:12, left:'50%', transform:'translateX(-50%)', display:'flex', gap:5 }}>
                    {photos.map((_, i) => (
                      <div key={i} onClick={() => setSelPhoto(i)}
                        style={{ width: selPhoto===i ? 18 : 6, height:6, borderRadius:3, background: selPhoto===i ? '#fff' : 'rgba(255,255,255,0.5)', transition:'all 0.3s', cursor:'pointer' }} />
                    ))}
                  </div>
                )}
              </div>

              {/* Thumbnail row */}
              {totalPhotos > 1 && (
                <div style={{ display:'flex', gap:6, overflowX:'auto', padding:'10px 0 4px', scrollbarWidth:'none' }}>
                  {photos.map((ph, i) => (
                    <div key={i} onClick={() => setSelPhoto(i)}
                      style={{ width:58, height:70, flexShrink:0, border: selPhoto===i ? '2px solid #7a1f1f' : '2px solid #e8ddd0', overflow:'hidden', cursor:'pointer' }}>
                      <img src={ph} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => { e.target.src = PH; }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ═══ INFO COLUMN ═══ */}
          <div>
            {product.category_name && (
              <Link to={`/collections/${product.category_slug}`} style={{ fontSize:10, letterSpacing:2, textTransform:'uppercase', color:'#c9a84c', display:'block', marginBottom:10 }}>
                {product.category_icon} {product.category_name}
              </Link>
            )}
            <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:'clamp(1.3rem,2.5vw,2rem)', color:'#1a0f0f', lineHeight:1.2, marginBottom:14 }}>{product.name}</h1>

            {/* Price */}
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:4, flexWrap:'wrap' }}>
              <span className="price-display price-xl"><span className="price-inr">₹</span>{formatINR(finalPrice)}</span>
              {product.discount_pct > 0 && <>
                <span className="price-strike">₹{formatINR(product.selling_price)}</span>
                <span className="price-save">Save {product.discount_pct}% OFF</span>
              </>}
            </div>
            {product.gst_rate > 0 && <div style={{ fontSize:11, color:'#8a7968', marginBottom:14, fontFamily:"'Jost',sans-serif" }}>Price inclusive of {product.gst_rate}% GST</div>}

            {/* Quick specs */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px 16px', background:'#f7f0e6', padding:14, marginBottom:14, borderLeft:'3px solid #c9a84c' }}>
              {[['Material',product.material],['Weave',product.weave_type],['Colour',product.color_shade],['Length',product.length_m && `${product.length_m} m`]].filter(r => r[1]).map(([k,v]) => (
                <div key={k}>
                  <div style={{ fontSize:9, letterSpacing:1.5, textTransform:'uppercase', color:'#8a7968', fontFamily:"'Jost',sans-serif" }}>{k}</div>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, color:'#1a0f0f', fontWeight:500 }}>{v}</div>
                </div>
              ))}
            </div>

            {/* Stock */}
            <div style={{ marginBottom:12, fontFamily:"'Jost',sans-serif", fontSize:13, fontWeight:600 }}>
              {product.stock_qty > 10 ? <span style={{ color:'#2e7d32' }}>✅ In Stock</span>
               : product.stock_qty > 0 ? <span style={{ color:'#e65100' }}>⚠️ Only {product.stock_qty} left!</span>
               : <span style={{ color:'#999' }}>❌ Out of Stock</span>}
            </div>

            {product.stock_qty > 0 && <>
              {/* Qty selector */}
              <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:12 }}>
                <span style={{ fontFamily:"'Jost',sans-serif", fontSize:11, letterSpacing:2, textTransform:'uppercase', color:'#8a7968' }}>Qty</span>
                <div style={{ display:'flex', alignItems:'center', border:'1.5px solid var(--border)' }}>
                  <button onClick={() => setQty(q => Math.max(1, q-1))} style={{ width:36, height:36, background:'#f7f0e6', border:'none', cursor:'pointer', fontSize:18 }}>−</button>
                  <span style={{ width:42, textAlign:'center', fontFamily:"'Cormorant Garamond',serif", fontSize:18 }}>{qty}</span>
                  <button onClick={() => setQty(q => Math.min(product.stock_qty, q+1))} style={{ width:36, height:36, background:'#f7f0e6', border:'none', cursor:'pointer', fontSize:18 }}>+</button>
                </div>
              </div>

              <div style={{ display:'flex', gap:10, marginBottom:10 }}>
                <button onClick={handleAddToCart} className="btn btn-primary" style={{ flex:1, justifyContent:'center', padding:'13px', fontSize:12 }}>🛒 Add to Cart</button>
                <button onClick={handleWishlist} className="btn btn-outline" style={{ padding:'13px 16px', fontSize:16 }}>❤️</button>
              </div>
              <button onClick={handleBuyNow} className="btn btn-gold" style={{ width:'100%', justifyContent:'center', padding:'13px', marginBottom:12 }}>⚡ Buy Now</button>

              {product.whatsapp_link && (
                <a href={product.whatsapp_link} target="_blank" rel="noopener noreferrer"
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, width:'100%', padding:'11px', background:'#25D366', color:'#fff', textDecoration:'none', fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:1, textTransform:'uppercase', fontWeight:600, marginBottom:14 }}>
                  💬 Enquire on WhatsApp
                </a>
              )}
            </>}

            {/* Pincode check */}
            <div style={{ padding:14, border:'1px dashed var(--border)', background:'#f7f0e6', marginBottom:14 }}>
              <div style={{ fontFamily:"'Jost',sans-serif", fontSize:12, fontWeight:600, marginBottom:8 }}>📍 Check Delivery</div>
              <div style={{ display:'flex', gap:8 }}>
                <input className="form-input" placeholder="6-digit Pincode" value={pincode}
                  onChange={e => setPincode(e.target.value.replace(/\D/g,'').slice(0,6))}
                  onKeyDown={e => e.key==='Enter' && checkPin()}
                  style={{ flex:1, padding:'8px 12px', fontSize:13 }} />
                <button onClick={checkPin} className="btn btn-outline" style={{ padding:'8px 14px', fontSize:11 }}>Check</button>
              </div>
              {pinMsg && <div style={{ marginTop:6, fontSize:12, color: pinMsg.includes('✅') ? '#2e7d32' : '#c00', fontFamily:"'Jost',sans-serif" }}>{pinMsg}</div>}
            </div>

            {/* Trust perks */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
              {[['🚚','Free delivery ₹999+'],['↩️','7-day returns'],['🏺','GI Certified'],['🔒','Secure pay']].map(([icon,txt]) => (
                <div key={txt} style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color:'#5a3a3a', fontFamily:"'Jost',sans-serif", padding:'6px 8px', background:'#f7f0e6' }}>
                  <span>{icon}</span><span>{txt}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── TABS ── */}
        <div style={{ borderTop:'2px solid var(--border)', paddingTop:32, marginTop:8 }}>
          <div style={{ display:'flex', borderBottom:'1px solid var(--border)', marginBottom:24, overflowX:'auto', scrollbarWidth:'none' }}>
            {[['description','Description'],['specifications','Specs'],['care','Care'],['reviews','Reviews']].map(([key,label]) => (
              <button key={key} onClick={() => setTab(key)}
                style={{ padding:'10px 18px', fontFamily:"'Jost',sans-serif", fontSize:11, letterSpacing:2, textTransform:'uppercase', background:'none', border:'none', cursor:'pointer', color: tab===key ? '#7a1f1f' : '#8a7968', borderBottom: tab===key ? '3px solid #7a1f1f' : '3px solid transparent', marginBottom:-1, fontWeight: tab===key ? 700 : 400, whiteSpace:'nowrap', flexShrink:0 }}>
                {label}
              </button>
            ))}
          </div>
          <div style={{ maxWidth:860 }}>
            {tab==='description' && (
              <div>
                <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, lineHeight:1.9, color:'#2d1f1f' }}>{product.description || 'Authentic Venkatagiri handloom — crafted with care and heritage.'}</p>
                {tags.filter(Boolean).length > 0 && <div style={{ marginTop:16 }}>{tags.filter(Boolean).map(t => <span key={t} style={{ display:'inline-block', background:'#f0e8e0', padding:'3px 10px', margin:'0 6px 6px 0', fontSize:12, color:'#7a1f1f' }}>{t.trim()}</span>)}</div>}
              </div>
            )}
            {tab==='specifications' && (
              <div>
                {[['Material',product.material],['Weave Type',product.weave_type],['Colour',product.color_shade],['Length',product.length_m&&`${product.length_m} m`],['Width',product.width_cm&&`${product.width_cm} cm`],['Weight',product.weight_g&&`${product.weight_g} g`],['Border',product.border_cm&&`${product.border_cm} cm`],['GST',`${product.gst_rate}%`],['SKU',product.sku]].map(([k,v]) => v ? (
                  <div key={k} style={{ display:'flex', padding:'11px 0', borderBottom:'1px solid #f0e8e0' }}>
                    <div style={{ width:160, fontSize:12, color:'#8a7968', textTransform:'uppercase', letterSpacing:0.5, fontFamily:"'Jost',sans-serif", flexShrink:0 }}>{k}</div>
                    <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:17, color:'#2d1f1f' }}>{v}</div>
                  </div>
                ) : null)}
              </div>
            )}
            {tab==='care' && (
              <div>
                {['🧼 Dry clean recommended for silk sarees','💧 Hand wash in cold water for cotton','☀️ Dry in shade — avoid direct sunlight','🌡️ Iron on low heat; use cloth on zari work','📦 Store in muslin cloth in cool, dry place','⚠️ Avoid perfume and direct chemical contact'].map(c => (
                  <div key={c} style={{ padding:'11px 0', borderBottom:'1px solid #f0e8e0', fontFamily:"'Cormorant Garamond',serif", fontSize:17, color:'#2d1f1f', lineHeight:1.6 }}>{c}</div>
                ))}
              </div>
            )}
            {tab==='reviews' && <ProductReviews productId={product.id} />}
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div style={{ marginTop:56 }}>
            <div style={{ marginBottom:28 }}>
              <div className="section-subtitle">You May Also Like</div>
              <h2 className="section-title">Related Products</h2>
              <div className="section-divider" />
            </div>
            <div className="scroll-row">{related.map(p => <ProductCard key={p.id} product={p} />)}</div>
          </div>
        )}
      </div>

      {/* ── STICKY BAR ── */}
      {sticky && product.stock_qty > 0 && (
        <div style={{ position:'fixed', bottom:0, left:0, right:0, background:'#fff', borderTop:'2px solid var(--border)', padding:'10px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, zIndex:500, boxShadow:'0 -4px 20px rgba(0,0,0,0.08)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, flex:1, minWidth:0 }}>
            <img src={photos[0]} alt="" style={{ width:40, height:50, objectFit:'cover', flexShrink:0 }} onError={e => { e.target.src = PH; }} />
            <div style={{ minWidth:0 }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:14, color:'#2d1f1f', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{product.name}</div>
              <div className="price-display" style={{ fontSize:17, color:'#7a1f1f', fontWeight:600 }}>₹{formatINR(finalPrice)}</div>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, flexShrink:0 }}>
            <button onClick={handleAddToCart} className="btn btn-primary" style={{ padding:'9px 14px', fontSize:11 }}>Add to Cart</button>
            <button onClick={handleBuyNow} className="btn btn-gold" style={{ padding:'9px 14px', fontSize:11 }}>Buy Now</button>
          </div>
        </div>
      )}

      {/* ── ZOOM LIGHTBOX ── */}
      {zoomOpen && (
        <div onClick={() => setZoomOpen(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.96)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:12 }}
          onTouchStart={onZoomTouchStart} onTouchEnd={onZoomTouchEnd}>

          <button onClick={() => setZoomOpen(false)}
            style={{ position:'absolute', top:14, right:14, color:'#fff', fontSize:22, cursor:'pointer', background:'rgba(255,255,255,0.12)', border:'none', width:40, height:40, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10 }}>✕</button>

          {totalPhotos > 1 && <>
            <button onClick={e => { e.stopPropagation(); prevZoom(); }}
              style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', width:42, height:42, borderRadius:'50%', fontSize:24, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10 }}>‹</button>
            <button onClick={e => { e.stopPropagation(); nextZoom(); }}
              style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', width:42, height:42, borderRadius:'50%', fontSize:24, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10 }}>›</button>
          </>}

          <div onClick={e => e.stopPropagation()} style={{ maxWidth:'90vw', maxHeight:'88vh' }}>
            <img src={photos[zoomPhoto]} alt={product.name}
              style={{ maxWidth:'90vw', maxHeight:'84vh', objectFit:'contain', display:'block', WebkitUserSelect:'none', userSelect:'none' }}
              onError={e => { e.target.src = PH; }} />
          </div>

          {totalPhotos > 1 && (
            <div style={{ position:'absolute', bottom:18, left:'50%', transform:'translateX(-50%)', display:'flex', gap:7 }}>
              {photos.map((_, i) => (
                <div key={i} onClick={e => { e.stopPropagation(); setZoomPhoto(i); }}
                  style={{ width: zoomPhoto===i ? 20 : 7, height:7, borderRadius:4, background: zoomPhoto===i ? '#fff' : 'rgba(255,255,255,0.4)', cursor:'pointer', transition:'all 0.3s' }} />
              ))}
            </div>
          )}

          <div style={{ position:'absolute', top:14, left:14, color:'rgba(255,255,255,0.65)', fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:1 }}>
            {zoomPhoto+1} / {totalPhotos}
          </div>
        </div>
      )}

      <style>{`
        /* Product page layout */
        .pp-grid { display:grid; grid-template-columns:1fr 1fr; gap:40px; margin-bottom:48px; }
        .pp-desk-gallery { display:flex; gap:12px; }
        .pp-mob-gallery  { display:none; }

        @media(max-width:900px){
          .pp-grid { grid-template-columns:1fr; gap:24px; }
          .pp-desk-gallery { display:none; }
          .pp-mob-gallery  { display:block; }
        }
      `}</style>
    </div>
  );
}
